import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import AllowedFor from '@/models/common/AllowedFor';
import { FundExpenseDetails } from '@/models/FundExpenseDetails';
import { getSupplierDisplayDetails, getPaymentBankDetails } from '@/helpers/supplierDisplayHelper';
import i18n from '@/i18n/i18n';

// CSV headers for APT import format (71 columns)
const CSV_HEADERS = [
  'IDAuto', 'מס_תורם', 'תאריך', 'שם_משפחה', 'שם_פרטי', 'שם', 'תואר_לפני', 'תואר_אחרי', 'שם_לחשבונית',
  'ת_זהות', 'כתובת', 'מס_בית', 'כניסה', 'דירה', 'ישוב', 'מיקוד', 'טלפון', 'טלפון2', 'אימייל', 'סכום',
  'מטבע', 'מתרים', 'תשלומים', 'מס_אישור', '4_ספרות', 'תוקף', 'מסלול', 'סוג_אשראי', 'מס_מסוף', 'טוקן',
  'מבצע', 'אסמכתא_לקוח', 'מס_פרוייקט', 'שם_פרוייקט', 'הערות', 'פרטים', 'תש_ראשון', 'תש_קבוע', 'מקור',
  'חברת_אשראי', 'מזהה_ייחודי', 'קופה', 'קוד_מבצע', 'מס_הוק', 'תשלומים_הוק', 'אמצעי', 'קוד_אירוע', 'בנק',
  'סניף', 'מס_חשבון', 'סיווג_תנועה', 'מתנה', 'מס_קבלה', 'סיווג1', 'סיווג2', 'סיווג3', 'מס_שיק', 'סוג_מסמך',
  'הערה_לקבלה', 'קוד_מנה', 'מס_אשראי', 'תאריך_ערך', 'מס_הלואה', 'מס_פיקדון', 'חשבון_חובה', 'בעל_החשבון',
  'שם_ב_זוג', 'תז_ב_זוג', 'CVV', 'קוד_תלמיד', 'סוג_עיסקה', 'התחייבות'
];

/**
 * Maps PaymentMethodType to APT payment method code
 * BankAccount -> 3 (העברה בנקאית)
 * CenterCard -> 15 (סנטר קארד)
 */
function getPaymentMethodCode(paymentMethodType: PaymentMethodType): string {
  switch (paymentMethodType) {
    case PaymentMethodType.BankAccount:
      return '3';
    default:
      return 'לא ידוע';
  }
}

/**
 * Gets the goal name translated to Hebrew
 */
function getGoalName(goalName: string | undefined): string {
  if (!goalName) return '';
  return i18n.t(goalName);
}

/**
 * Formats date as dd/mm/yyyy
 */
function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Escapes CSV field values
 */
function escapeCSVField(value: string | number | undefined | null): string {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

/**
 * Converts a support request to a CSV row array
 */
function requestToCSVRow(request: ManagementSupportRequestDtoIn): string[] {
  const row = new Array(CSV_HEADERS.length).fill('');

  // Column 2 (index 2): Date - today's date
  row[2] = formatDate(new Date());

  // Columns 3, 4, 9: Name and ID - depends on request type
  if (request.goal?.allowedFor === AllowedFor.Fund) {
    // Fund Expense: use supplier or payer details based on isAlreadyPaid
    const details = request.additionalDetails as FundExpenseDetails | undefined;
    const supplierDisplay = getSupplierDisplayDetails(request.supplier, request.additionalDetails);
    if (details?.isAlreadyPaid) {
      row[3] = details?.payerName ?? '';
      row[4] = '';
      row[9] = details?.payerIsraeliId ?? '';
    } else {
      row[3] = supplierDisplay?.name ?? '';
      row[4] = '';
      row[9] = supplierDisplay?.taxId ?? '';
    }
  } else {
    // Regular support request: use beneficiary contact
    row[3] = request.beneficiaryContact?.lastName ?? '';
    row[4] = request.beneficiaryContact?.firstName ?? '';
    row[9] = request.beneficiaryContact?.israeliId ?? '';
  }

  // Column 19 (index 19): Approved Amount
  row[19] = String(request.approvedAmount);

  // Column 20 (index 20): Currency (1 = NIS)
  row[20] = '1';

  // Column 22 (index 22): Payment count
  row[22] = '1';

  // Column 34 (index 34): Goal name + " - מהאתר החדש"
  row[34] = getGoalName(request.goal?.name) + ' - מהאתר החדש';

  // Column 37 (index 37): Fixed payment amount (same as approved)
  row[37] = String(request.approvedAmount);

  // Column 41 (index 41): Fund ID
  row[41] = String(request.fundId);

  // Column 45 (index 45): Payment method code
  if (request.goal?.allowedFor === AllowedFor.Fund) {
    // Fund Expense: always bank transfer since it has bank details
    row[45] = '3';
  } else {
    row[45] = request.paymentMethodType ? getPaymentMethodCode(request.paymentMethodType) : '';
  }

  // Columns 47, 48, 49: Bank details - depends on request type
  if (request.goal?.allowedFor === AllowedFor.Fund) {
    // Fund Expense: payment destination depends on isAlreadyPaid (payer's bank when
    // reimbursing, vendor's bank otherwise). Vendor bank comes from the linked PaymentInfo
    // (exposed on the request DTO as bankNumber/bankBranchNumber/bankAccountNumber); the
    // payer's bank lives on AdditionalDetails. Falls back to legacy snapshot when needed.
    const paymentBank = getPaymentBankDetails(
      {
        bankCode: request.bankNumber,
        bankBranch: request.bankBranchNumber,
        bankAccount: request.bankAccountNumber,
      },
      request.additionalDetails,
    );
    row[47] = paymentBank?.bankCode ? String(paymentBank.bankCode) : '';
    row[48] = paymentBank?.bankBranch || '';
    row[49] = paymentBank?.bankAccount || '';
  } else {
    // Regular support request: use bank details from request
    row[47] = request.bankNumber ? String(request.bankNumber) : '';
    row[48] = request.bankBranchNumber || '';
    row[49] = request.bankAccountNumber || '';
  }

  // Column 50 (index 50): Classification
  if (request.goal?.allowedFor === AllowedFor.Fund) {
    row[50] = 'תשלום';
  } else {
    row[50] = request.multiRequestTotalCount === 1 ? 'תמיכה' : 'קבוע חודשי';
  }

  return row;
}

/**
 * Exports selected support requests to CSV format for APT import
 */
export function exportSupportRequestsToCsv(requests: ManagementSupportRequestDtoIn[]): void {
  if (requests.length === 0) {
    alert('יש לבחור שורות לייצוא CSV');
    return;
  }

  // Build CSV content
  const rows: string[][] = [CSV_HEADERS];

  for (const request of requests) {
    rows.push(requestToCSVRow(request));
  }

  // Convert to CSV string with BOM for Hebrew support
  const BOM = '\uFEFF';
  const csvContent = BOM + rows.map(row => row.map(escapeCSVField).join(',')).join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', 'בקשות תמיכה.csv');
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

